/* Automated Quality Response — persistent rule failures, escalated to incidents.
 *
 * Laid out as the redesign draws it: the posture beside the observation period, four tiles, the
 * detection log, and what the engine does and never does. Every `{{ }}` in the design is filled here
 * from the real `/v1/ai/autonomous` responses, including three states the design's fixtures never
 * had to represent — a tri-state permission, an absent rule name or run count on a row carried over
 * from the design this module replaced, and a threshold of zero on a reversal recorded under it.
 *
 * Five judgements worth not reversing.
 *
 * **"Would have escalated" is its own outcome, and the count a reviewer reads first.** Those are
 * the rows the gate permitted and the mode withheld — precisely what will escalate the day
 * escalation is turned on. Folding them into either neighbour would bury the only rows an
 * observation period is really about.
 *
 * **"Alerts not routed" is the one tile allowed a colour.** An incident that reached nobody looks
 * identical to a healthy estate on every other surface, so that is where the amber earns itself —
 * and where the way to fix it is offered.
 *
 * **Every clock figure comes from the API.** `observation_ends_at`, `days_remaining` and
 * `can_enable` are computed server-side from a period the substrate bounds, so the screen cannot
 * disagree with the door about whether it is open — and the door is a CHECK regardless of what any
 * client renders.
 *
 * **The period card and the not-permitted band read two different facts.** Whether the viewer may
 * change anything is a *permission*; whether the period is still editable is a *lifecycle*
 * question, closed once escalation is on. Folding them into one flag makes the note about needing
 * an administrator appear to somebody who has just used the button.
 *
 * **A row carried over from this module's earlier design says so, rather than printing zeros.** That
 * design stored a confidence and a rationale and nothing to reconstruct run counts or a rule name
 * from, so a raw render reads `0 of 0 failing` beside an 83% confidence — an arithmetic the platform
 * could not have performed. `evidenceRecorded`/`ruleLabel`/`thresholdRecorded` name the absence
 * instead of asserting it as a measurement.
 */

import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, generatePath } from 'react-router-dom';

import { can, useIdentity } from '@/entities/session';
import type { Detection, ModeReport } from '@/shared/api/generated/ai-autonomous';
import { useCursorStack } from '@/shared/api/paging';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { SCREEN_PATHS } from '@/shared/routes';
import { Alert } from '@/shared/ui/Alert';
import { PageHeader } from '@/shared/ui/PageHeader';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  useCapabilities,
  useConfigureObservationPeriod,
  useDetectionPage,
  useEnableEscalation,
  useMode,
  useSummary,
} from '../api/autonomous';
import {
  alertReading,
  alertTone,
  canSavePeriod,
  clearedThreshold,
  dayOf,
  evidenceRecorded,
  formatConfidence,
  outcomeOf,
  outcomeTone,
  periodChangeOf,
  periodEdit,
  periodIsRefusable,
  progressPercent,
  ruleLabel,
  talliesOf,
  thresholdRecorded,
} from '../model/autonomous';

/** The permission that gates both controls. Admin-only server-side; hiding them here only avoids
 * offering a button a click would be refused for. */
const CONFIGURE_PERMISSION = 'configure:ai.autonomous';

/* Day first, as the design writes it ("29 Aug 2026"). `toLocaleDateString()` with no locale took
 * the browser's, so one tenant's screen read "8/29/2026" and another's "29/08/2026" for the same
 * instant — and the end of an observation period is a date somebody plans around. */
const DAY = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
});

const INSTANT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

function parsed(iso: string): Date | null {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

function instant(iso: string): string {
  const at = parsed(iso);
  return at === null ? iso : INSTANT.format(at);
}

function day(iso: string): string {
  const at = parsed(iso);
  return at === null ? iso : DAY.format(at);
}

/* ------------------------------------------------------------------------- the posture card */

function Posture({
  report,
  permitted,
}: {
  readonly report: ModeReport;
  readonly permitted: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const enable = useEnableEscalation();
  const [now] = useState(() => new Date());
  const escalating = !report.observing;
  const remaining = report.days_remaining;
  const run = !escalating && remaining === 0;

  return (
    <section className="lq-cp__card lq-au__posture" aria-labelledby="au-posture">
      <div className="lq-au__postureHead">
        <span
          id="au-posture"
          className={escalating ? 'lq-tag lq-tag--au-on' : 'lq-tag lq-tag--au-observing'}
        >
          {t(escalating ? 'autonomous.posture.active' : 'autonomous.posture.observing')}
        </span>
        <span className="lq-au__postureNote">
          {t(
            escalating
              ? 'autonomous.posture.activeMeans'
              : 'autonomous.posture.observingMeans',
          )}
        </span>
      </div>

      <div className="lq-au__day">
        {t('autonomous.bar.dayOf', {
          day: dayOf(report, now),
          total: report.observation_days,
        })}
      </div>
      {/* The unelapsed part of the period is the hatch every "not yet" track wears; the elapsed part
          fills in the accent. */}
      <div className="lq-au__track">
        <div
          className="lq-au__fill"
          style={{ width: `${String(progressPercent(report, now))}%` }}
        />
      </div>

      <dl className="lq-au__facts">
        <div className="lq-au__fact">
          <dt>{t('autonomous.fact.started')}</dt>
          <dd>{day(report.shadow_started_at)}</dd>
        </div>
        <div className="lq-au__fact">
          <dt>{t('autonomous.fact.period')}</dt>
          <dd>{t('autonomous.fact.days', { count: report.observation_days })}</dd>
        </div>
        <div className="lq-au__fact">
          <dt>
            {t(escalating || run ? 'autonomous.fact.ended' : 'autonomous.fact.ends')}
          </dt>
          <dd>{day(report.observation_ends_at)}</dd>
        </div>
        {escalating ? (
          <>
            {/* The enablement instant and who chose it, not a remaining figure: once escalating
                there is no window left to run, so a countdown would be a number with no meaning. */}
            <div className="lq-au__fact">
              <dt>{t('autonomous.fact.since')}</dt>
              <dd>
                {report.escalation_enabled_at === null
                  ? '—'
                  : day(report.escalation_enabled_at)}
              </dd>
            </div>
            <div className="lq-au__fact">
              <dt>{t('autonomous.fact.by')}</dt>
              <dd title={report.enabled_by ?? undefined}>
                {report.enabled_by === null ? '—' : personName(report.enabled_by)}
              </dd>
            </div>
          </>
        ) : (
          <div className="lq-au__fact">
            <dt>{t('autonomous.fact.remaining')}</dt>
            <dd>{t('autonomous.fact.days', { count: remaining })}</dd>
          </div>
        )}
      </dl>

      {run && <p className="lq-au__line">{t('autonomous.ready')}</p>}
      {/* The button only when the platform says the door is open, and its own refusal when it does
          not — so a control and a reason it would fail are never both on screen. */}
      {run && !report.can_enable && report.enable_blocked_reason !== null && (
        <p className="lq-au__line">{report.enable_blocked_reason}</p>
      )}
      {run && report.can_enable && permitted && !confirming && (
        <button
          type="button"
          className="lq-cp__headButton lq-au__turnOn"
          onClick={() => {
            setConfirming(true);
          }}
        >
          {t('autonomous.enable')}
        </button>
      )}

      {confirming && (
        <div className="lq-au__ask" role="group" aria-labelledby="au-ask">
          <strong id="au-ask">{t('autonomous.confirm.title')}</strong>
          <span>{t('autonomous.confirm.body')}</span>
          {enable.isError && (
            <Alert tone="error" title={t('autonomous.confirm.failed')}>
              {enable.error.message}
            </Alert>
          )}
          <div className="lq-au__askActions">
            <button
              type="button"
              className="lq-cp__headButton"
              onClick={() => {
                setConfirming(false);
              }}
            >
              {t('autonomous.confirm.cancel')}
            </button>
            <button
              type="button"
              className="lq-au__confirm"
              disabled={enable.isPending}
              onClick={() => {
                enable.mutate(undefined, {
                  onSuccess: () => {
                    setConfirming(false);
                  },
                });
              }}
            >
              {t('autonomous.enable')}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------- the observation period */

function Period({
  report,
  editable,
}: {
  readonly report: ModeReport;
  readonly editable: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [raw, setRaw] = useState(String(report.observation_days));
  const save = useConfigureObservationPeriod();
  const edit = periodEdit(raw, report);
  const bad = periodIsRefusable(edit);
  const bounds = t('autonomous.period.range', {
    min: report.min_observation_days,
    max: report.max_observation_days,
  });
  const change = periodChangeOf(report);

  return (
    <section className="lq-cp__card lq-au__period" aria-labelledby="au-period">
      <h2 id="au-period" className="lq-cp__cardTitle">
        {t('autonomous.period.title')}
      </h2>
      <p className="lq-au__line">{t('autonomous.period.body')}</p>

      {editable ? (
        <>
          <div className="lq-au__periodRow">
            <input
              className="lq-au__periodInput"
              value={raw}
              inputMode="numeric"
              aria-label={t('autonomous.period.title')}
              aria-invalid={bad}
              onChange={(event) => {
                setRaw(event.target.value);
              }}
            />
            <span className="lq-au__periodUnit">{t('autonomous.period.unit')}</span>
            <button
              type="button"
              className="lq-cp__headButton lq-au__save"
              disabled={!canSavePeriod(edit) || save.isPending}
              onClick={() => {
                if (canSavePeriod(edit)) save.mutate(Number.parseInt(raw, 10));
              }}
            >
              {t('autonomous.period.save')}
            </button>
          </div>
          {bad && (
            <p className="lq-au__periodError">
              {edit.kind === 'would-shorten'
                ? t('autonomous.period.cannotShorten', { count: edit.current })
                : bounds}
            </p>
          )}
          <p className="lq-au__note">
            {bounds} {t('autonomous.period.noRestart')}
          </p>
          {save.isError && (
            <Alert tone="error" title={t('autonomous.period.failed')}>
              {save.error.message}
            </Alert>
          )}
        </>
      ) : (
        <p className="lq-au__note">
          {/* Two different reasons it is read-only, and they are not interchangeable: a closed
              lifecycle once escalation is on, or a permission the viewer does not hold. */}
          {bounds}{' '}
          {t(
            report.observing ? 'autonomous.period.adminOnly' : 'autonomous.period.closed',
          )}
        </p>
      )}

      {/* "Last changed {date} by {actor}", or nothing — never fabricated for a tenant that has
          always been on the default. */}
      {change === null ? null : (
        <p className="lq-au__note">
          {t('autonomous.period.lastChanged', {
            date: day(change.at),
            by: personName(change.by),
          })}
        </p>
      )}
    </section>
  );
}

/* --------------------------------------------------------------------------------- the tiles */

function Tiles({ observing }: { readonly observing: boolean }): ReactElement | null {
  const { t } = useTranslation();
  const summary = useSummary();
  if (summary.data === undefined) return null;
  const counts = talliesOf(summary.data);
  const unrouted = counts.notRouted > 0;

  const tiles = [
    { key: 'persistent', value: counts.persistent, note: null },
    {
      key: 'incidents',
      value: counts.incidents,
      note: observing ? t('autonomous.tile.incidentsObserving') : null,
    },
    { key: 'notified', value: counts.notified, note: null },
    {
      key: 'notRouted',
      value: counts.notRouted,
      note: t('autonomous.tile.notRoutedNote'),
    },
  ] as const;

  return (
    <div className="lq-au__tiles">
      {tiles.map((tile) => (
        <div
          key={tile.key}
          className={
            /* The only tile that may colour, and only when non-zero. */
            tile.key === 'notRouted' && unrouted
              ? 'lq-au__tile lq-au__tile--warn'
              : 'lq-au__tile'
          }
        >
          <div className="lq-au__tileKey">{t(`autonomous.tile.${tile.key}`)}</div>
          <div className="lq-au__tileValue">{tile.value.toLocaleString()}</div>
          {tile.note !== null && <div className="lq-au__tileNote">{tile.note}</div>}
          {/* An incident that reached nobody is fixed on the routing screen, so the way there sits
              with the count that says it is needed — and only then. */}
          {tile.key === 'notRouted' && unrouted && (
            <Link className="lq-au__tileLink" to={SCREEN_PATHS.alertRouting}>
              {t('autonomous.unrouted.link')}
            </Link>
          )}
        </div>
      ))}
    </div>
  );
}

/* ----------------------------------------------------------------------------------- the log */

function Row({
  detection,
  open,
  onToggle,
  now,
}: {
  readonly detection: Detection;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly now: Date;
}): ReactElement {
  const { t } = useTranslation();
  const outcome = outcomeOf(detection);
  const reading = alertReading(detection);
  const name = ruleLabel(detection);
  const measured = evidenceRecorded(detection);

  return (
    <>
      <tr
        className={open ? 'lq-au__row lq-au__row--open' : 'lq-au__row'}
        aria-expanded={open}
        onClick={onToggle}
      >
        <td data-label={t('autonomous.column.outcome')}>
          <span
            className={`lq-tag lq-tag--au-${outcomeTone(outcome)}`}
            title={t(`autonomous.outcomeMeans.${outcome}`)}
          >
            {t(`autonomous.outcome.${outcome}`)}
          </span>
        </td>
        <td
          data-label={t('autonomous.column.rule')}
          title={detection.dataset_label ?? undefined}
        >
          {/* The rule's name, never the binding id: a UUID in the cell a steward reads first is
              the raw-id defect this codebase records. An absent name is named rather than left
              blank, because a blank cell reads as a screen that failed. */}
          {name ?? (
            <span className="lq-muted" title={detection.binding_id}>
              {t('autonomous.ruleUnknown')}
            </span>
          )}
        </td>
        <td data-label={t('autonomous.column.evidence')} className="lq-au__evidence">
          {/* Zeros here would be an arithmetic the platform could not have performed — see
              `evidenceRecorded`. The confidence beside it was recorded and is still shown. */}
          {measured ? (
            <>
              {t('autonomous.evidence', {
                failing: detection.failing_runs,
                runs: detection.runs,
              })}
              {detection.violations > 0
                ? ` · ${t('autonomous.violations', { count: detection.violations })}`
                : ''}
            </>
          ) : (
            <span className="lq-muted">{t('autonomous.evidenceUnrecorded')}</span>
          )}
        </td>
        <td data-label={t('autonomous.column.confidence')} className="lq-nowrap">
          <strong
            className={
              clearedThreshold(detection) ? 'lq-au__conf' : 'lq-au__conf lq-muted'
            }
          >
            {formatConfidence(detection.confidence)}
          </strong>
          {thresholdRecorded(detection) && (
            <span className="lq-au__needs">
              {' '}
              {t('autonomous.needs', {
                threshold: formatConfidence(detection.threshold),
              })}
            </span>
          )}
        </td>
        <td data-label={t('autonomous.column.incident')}>
          {/* Shortened with the full value on hover. An incident has no name, so this is the
              last-resort case the raw-id rule allows — and it opens the incident. */}
          {detection.incident_id === null ? (
            <span className="lq-muted">—</span>
          ) : (
            <Link
              className="lq-au__incident"
              title={detection.incident_id}
              to={generatePath(SCREEN_PATHS.incident, {
                incidentId: detection.incident_id,
              })}
              onClick={(event) => {
                event.stopPropagation();
              }}
            >
              {t('autonomous.incidentRef', { id: detection.incident_id.slice(0, 8) })}
            </Link>
          )}
        </td>
        <td
          data-label={t('autonomous.column.alert')}
          className={`lq-au__alert lq-au__alert--${alertTone(reading)}`}
          title={detection.notify_detail ?? undefined}
        >
          {reading === 'not-attempted' ? '—' : t(`autonomous.alert.${reading}`)}
        </td>
        <td
          data-label={t('autonomous.column.detected')}
          className="lq-au__when"
          title={instant(detection.detected_at)}
        >
          {ageText(t, detection.detected_at, now) ?? instant(detection.detected_at)}
        </td>
      </tr>
      {open && (
        <tr className="lq-au__detailRow">
          <td colSpan={7}>
            {/* Verbatim, as the platform wrote it. No model text exists in this module, and
                paraphrasing arithmetic would put a second author between the reader and the
                decision. */}
            <span className="lq-au__detailKey">{t('autonomous.detail.rationale')}</span>
            {' — '}
            {detection.rationale}
          </td>
        </tr>
      )}
    </>
  );
}

function Log({ assessed }: { readonly assessed: boolean }): ReactElement {
  const { t } = useTranslation();
  const pager = useCursorStack();
  const page = useDetectionPage(pager.cursor);
  const [open, setOpen] = useState<readonly string[]>([]);
  /* One clock per page, so two detections from the same sweep cannot read as different ages. */
  const [now] = useState(() => new Date());

  const detections = page.data?.detections ?? [];

  return (
    <section className="lq-cp__card lq-cp__card--table" aria-labelledby="au-log">
      <div className="lq-au__logHead">
        <h2 id="au-log" className="lq-cp__cardTitle">
          {t('autonomous.logTitle')}
        </h2>
        <p className="lq-cp__muted">{t('autonomous.logNote')}</p>
      </div>

      {/* **Not assessed is not empty.** A tenant the engine has never evaluated has no log yet, and
          "no persistent failures" there would be a claim about data nobody has read. */}
      {!assessed ? (
        <p className="lq-au__none">
          {t('autonomous.neverEvaluatedLog')}. {t('autonomous.neverEvaluatedLogNote')}
        </p>
      ) : page.isPending ? (
        <SkeletonRows rows={5} columns={7} />
      ) : page.isError ? (
        <div className="lq-au__logBody">
          <Alert tone="error" title={t('autonomous.logFailed')}>
            {page.error.message}
          </Alert>
        </div>
      ) : (
        <>
          <div className="lq-cp__scroll">
            <table className="lq-table lq-table--ledger lq-cp__table lq-au__log">
              <caption className="lq-visually-hidden">{t('autonomous.logTitle')}</caption>
              <thead>
                <tr>
                  <th scope="col">{t('autonomous.column.outcome')}</th>
                  <th scope="col">{t('autonomous.column.rule')}</th>
                  <th scope="col">{t('autonomous.column.evidence')}</th>
                  <th scope="col">{t('autonomous.column.confidence')}</th>
                  <th scope="col">{t('autonomous.column.incident')}</th>
                  <th scope="col">{t('autonomous.column.alert')}</th>
                  <th scope="col">{t('autonomous.column.detected')}</th>
                </tr>
              </thead>
              <tbody>
                {detections.map((detection) => (
                  <Row
                    key={detection.id}
                    detection={detection}
                    open={open.includes(detection.id)}
                    now={now}
                    onToggle={() => {
                      setOpen((current) =>
                        current.includes(detection.id)
                          ? current.filter((id) => id !== detection.id)
                          : [...current, detection.id],
                      );
                    }}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {detections.length === 0 ? (
            /* On page 1 this is the healthy state — nothing is failing consistently enough to
               escalate. Past page 1 the walk has simply run out, which is a different sentence. */
            <p className="lq-au__none">
              {t(pager.canGoBack ? 'autonomous.endOfLog' : 'autonomous.nothingFound')}.{' '}
              {t(
                pager.canGoBack
                  ? 'autonomous.endOfLogNote'
                  : 'autonomous.nothingFoundNote',
              )}
            </p>
          ) : null}
          {/* A page number and never a total: a keyset listing cannot know how many pages exist
              without counting the whole table, and a denominator would be a truncation wearing a
              fraction. Shown only when there is somewhere to go. */}
          {pager.canGoBack || page.data.nextCursor !== null ? (
            <div className="lq-au__pager">
              <span className="lq-au__pageLabel">
                {t('autonomous.pager.page', { page: pager.page })}
              </span>
              <button
                type="button"
                className="lq-cp__headButton"
                disabled={!pager.canGoBack}
                onClick={pager.back}
              >
                {t('autonomous.pager.previous')}
              </button>
              <button
                type="button"
                className="lq-cp__headButton"
                disabled={page.data.nextCursor === null}
                onClick={() => {
                  pager.forward(page.data.nextCursor);
                }}
              >
                {t('autonomous.pager.next')}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

/* ------------------------------------------------------------------------- the boundary cards */

function Boundary(): ReactElement | null {
  const { t } = useTranslation();
  const { data } = useCapabilities();
  if (data === undefined) return null;

  return (
    <div className="lq-au__boundary">
      <section className="lq-cp__card lq-au__bound" aria-labelledby="au-does">
        <h2 id="au-does" className="lq-cp__cardTitle">
          {t('autonomous.boundary.does')}
        </h2>
        <ul className="lq-au__boundList">
          {data.automatic_actions.map((action) => (
            <li key={action}>{action}</li>
          ))}
        </ul>
      </section>
      <section className="lq-cp__card lq-au__bound" aria-labelledby="au-never">
        <h2 id="au-never" className="lq-cp__cardTitle">
          {t('autonomous.boundary.never')}
        </h2>
        <ul className="lq-au__boundList">
          {data.never_does.map((entry) => (
            <li key={entry}>{entry}</li>
          ))}
        </ul>
        {/* The deployment's own sentence about models, verbatim: it is the claim a reader is most
            likely to ask about, so it is shown in the platform's words. */}
        <p className="lq-au__note">{data.model_involvement}</p>
      </section>
    </div>
  );
}

/* -------------------------------------------------------------------------------- the screen */

export function AutonomousLog(): ReactElement {
  const { t } = useTranslation();
  const mode = useMode();
  const identity = useIdentity();

  /* **Three-state standing, not a boolean.** `can()` reads false both while the identity query is
   * in flight and once it is denied, so a boolean would flash "you need an administrator" at
   * somebody who is one. Nothing is claimed until the read lands. */
  const standing: 'unknown' | 'forbidden' | 'permitted' =
    identity.isPending || identity.isError
      ? 'unknown'
      : can(identity.data, CONFIGURE_PERMISSION)
        ? 'permitted'
        : 'forbidden';

  const report = mode.data?.report ?? null;
  const observing = report === null || report.observing;
  /* Two facts, kept apart: a *permission* drives the band, and the *lifecycle* — closed once
     escalation is on — drives whether the card takes input. */
  const editable = standing === 'permitted' && observing;

  return (
    <div className="lq-au">
      <PageHeader title={t('autonomous.title')} lede={t('autonomous.subtitle')} />

      {standing === 'forbidden' && (
        <p className="lq-tintband lq-tintband--info">
          <span className="lq-tintband__text">{t('autonomous.readOnly.body')}</span>
        </p>
      )}

      {mode.isPending ? (
        <SkeletonRows rows={3} columns={4} />
      ) : mode.isError ? (
        <Alert tone="error" title={t('autonomous.modeFailed')}>
          {mode.error.message}
        </Alert>
      ) : report === null ? (
        /* Never evaluated. A real state rather than an error, and distinct from day 0 — the clock
           starts when detecting starts, so an invented start would make them look the same. */
        <p className="lq-tintband lq-tintband--info">
          <span className="lq-tintband__text">
            <strong>{t('autonomous.neverEvaluated')}.</strong>{' '}
            {mode.data.detail ?? t('autonomous.neverEvaluatedFallback')}
          </span>
        </p>
      ) : (
        <div className="lq-au__top">
          <Posture report={report} permitted={standing === 'permitted'} />
          {standing !== 'unknown' && <Period report={report} editable={editable} />}
        </div>
      )}

      <Tiles observing={observing} />

      {/* **`isSuccess`, not `report === null`.** A *failed* posture read also leaves `report`
          null, and an earlier version reported that as "nothing has been assessed yet" — a claim
          about the data used to report a failure of a request. The log is its own route and reads
          for itself unless the posture positively says nothing has been assessed. */}
      <Log assessed={!(mode.isSuccess && mode.data.report === null)} />

      <Boundary />
    </div>
  );
}
